import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import logo from "../assets/img/CheckIDBioSense_light.svg";
import qrcode from "../assets/img/qrdownload.png";
import { LeafButtonNew, PoppyButtonNew } from "../components/button";
import { RiAppStoreFill, RiGooglePlayFill } from "react-icons/ri";

import step1android from "../assets/android/step1.png";
import step2android from "../assets/android/step2.png";
import step3android from "../assets/android/step3.png";
import step4android from "../assets/android/step4.png";
import step5android from "../assets/android/step5.png";
import step6android from "../assets/android/step6.png";

import step1ios from "../assets/ios/ios1.png";
import step2ios from "../assets/ios/ios2.png";
import step3ios from "../assets/ios/ios3.png";
import step4ios from "../assets/ios/ios4.png";
import step5ios from "../assets/ios/ios5.png";

const Downloads = () => {
    const { t } = useTranslation();

    const [open, setOpen] = useState(false);
    const [type, setType] = useState(null);
    const [current, setCurrent] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [platform, setPlatform] = useState("android");

    const startX = useRef(0);
    const isDragging = useRef(false);

    const androidImages = [
        
        step1android,
        step2android,
        step3android,
        step4android,
        step5android,
        step6android,
    ];

    // ví dụ (bạn thay bằng ảnh iOS thật)
    const iosImages = [
        step1ios,
        step2ios,
        step3ios,
        step4ios,
        step5ios,
    ];

    // =======================
    // HANDLERS
    // =======================

    const handleOpen = (value) => {
        setType(value);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setType(null);
    };

    const handleNext = () => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrent((prev) => (prev + 1) % images.length);
        setTimeout(() => setIsAnimating(false), 500);
    };

    const handlePrev = () => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrent((prev) => (prev - 1 + images.length) % images.length);
        setTimeout(() => setIsAnimating(false), 500);
    };

    // =======================
    // SWIPE MOBILE
    // =======================

    const handleTouchStart = (e) => {
        startX.current = e.touches[0].clientX;
        isDragging.current = true;
    };

    const handleTouchMove = (e) => {
        if (!isDragging.current) return;

        const diff = e.touches[0].clientX - startX.current;

        if (Math.abs(diff) > 50) {
            if (diff > 0) handlePrev();
            else handleNext();

            isDragging.current = false;
        }
    };

    const handleTouchEnd = () => {
        isDragging.current = false;
    };

    // =======================
    // AUTO SLIDE (optional)
    // =======================

    useEffect(() => {
        const interval = setInterval(() => {
            handleNext();
        }, 4000);

        return () => clearInterval(interval);
    }, [isAnimating]);

    // =======================
    // TITLE
    // =======================

    const modalTitle = type
        ? `${t("downloads.download")} ${t(`downloads.${type}`)}`
        : t("downloads.download");

    const images = platform === "android" ? androidImages : iosImages;

    useEffect(() => {
        setCurrent(0);
    }, [platform]);
    // =======================
    // UI
    // =======================

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-10 md:py-20 bg-gray-50 overflow-y-auto mx-auto">
            <div className="w-full max-w-3xl text-center">

                {/* Logo */}
                <img
                    src={logo}
                    className="h-[90px] mx-auto mb-6 object-contain"
                    alt="logo"
                />

                {/* QR */}
                <img
                    src={qrcode}
                    className="h-[180px] w-[180px] mx-auto mb-8"
                    alt="qr"
                />

                {/* Buttons */}
                <div className="flex gap-4 justify-center mb-8">
                    <LeafButtonNew
                        title={t("downloads.production")}
                        onClick={() => handleOpen("production")}
                    />
                    <PoppyButtonNew
                        title={t("downloads.demo")}
                        onClick={() => handleOpen("demo")}
                    />
                </div>

                {/* Text */}
                <div className="text-gray-600 space-y-1 max-w-2xl mx-auto leading-relaxed px-2">
                    <p className="font-semibold text-lg text-gray-800">
                        {t("downloads.title")}
                    </p>
                    <p className="text-md">
                        {t("downloads.description")}
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex justify-center gap-2 mt-6 mb-4">
                    <button
                        onClick={() => setPlatform("android")}
                        className={`px-4 w-[100px] py-2 rounded-full text-sm font-medium transition ${platform === "android"
                            ? "bg-[var(--tree-leaf)] text-white"
                            : "bg-gray-200 text-gray-600"
                            }`}
                    >
                        Android
                    </button>

                    <button
                        onClick={() => setPlatform("ios")}
                        className={`px-4 w-[100px] py-2 rounded-full text-sm font-medium transition ${platform === "ios"
                            ? "bg-[var(--tree-leaf)] text-white"
                            : "bg-gray-200 text-gray-600"
                            }`}
                    >
                        iOS
                    </button>
                </div>

                {/* ================= SLIDER ================= */}
                <div className="mt-6 flex flex-col items-center">

                    <div className="flex items-center justify-center w-full mx-auto max-w-xl">

                        {/* Prev */}
                        <button
                            onClick={handlePrev}
                            className="md:hidden w-10 md:w-12 flex justify-center text-3xl md:text-4xl text-gray-400 hover:text-black hover:scale-110 transition"
                        >
                            ‹
                        </button>

                        {/* Slider */}
                        <div className="flex-1 flex justify-center">
                            <div
                                className="w-[260px] md:w-[350px] overflow-hidden touch-pan-y"
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                            >
                                <div
                                    className="flex transition-transform duration-500 ease-in-out"
                                    style={{ transform: `translateX(-${current * 100}%)` }}
                                >
                                    {images.map((img, index) => (
                                        <img
                                            key={index}
                                            src={img}
                                            alt={`step-${index}`}
                                            className="w-[260px] md:w-[350px] flex-shrink-0 rounded-xl shadow-md object-contain"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Next */}
                        <button
                            onClick={handleNext}
                            className="md:hidden w-10 md:w-12 flex justify-center text-3xl md:text-4xl text-gray-400 hover:text-black hover:scale-110 transition"
                        >
                            ›
                        </button>
                    </div>

                    {/* Dots */}
                    <div className="flex gap-2 mt-4">
                        {images.map((_, index) => (
                            <div
                                key={index}
                                onClick={() => setCurrent(index)}
                                className={`h-2.5 w-2.5 rounded-full cursor-pointer transition ${index === current
                                    ? "bg-black scale-110"
                                    : "bg-gray-300"
                                    }`}
                            />
                        ))}
                    </div>
                </div>

                {/* ================= MODAL ================= */}
                {open && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4 overflow-y-auto">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center shadow-xl my-10">

                            <h2 className="text-lg md:text-xl font-semibold mb-5">
                                {modalTitle}
                            </h2>

                            <div className="flex flex-col gap-4">
                                {/* production → /downloads/production/… ; demo → /downloads/demo/… */}
                                <a
                                    href={`itms-services://?action=download-manifest&url=https://biosense.mobile-id.vn/downloads/${type}/ipa/ipad.plist`}
                                    className="flex items-center justify-center gap-2 text-white py-3 rounded-xl text-sm md:text-base font-medium transition hover:opacity-90"
                                    style={{ backgroundColor: "var(--tree-leaf)" }}
                                >
                                    <RiAppStoreFill size={20} />
                                    {t("downloads.ios")}
                                </a>

                                <a
                                    href={`https://biosense.mobile-id.vn/downloads/${type}/apk/biosense.apk`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 text-white py-3 rounded-xl text-sm md:text-base font-medium transition hover:opacity-90"
                                    style={{ backgroundColor: "var(--tree-poppy)" }}
                                >
                                    <RiGooglePlayFill size={18} />
                                    {t("downloads.android")}
                                </a>
                            </div>

                            <button
                                onClick={handleClose}
                                className="mt-6 text-gray-400 hover:text-gray-600 text-sm transition"
                            >
                                {t("common.close")}
                            </button>

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Downloads;